import styles from './Shift.module.css';

import { DateTime } from 'luxon';
import { useState, useEffect } from 'react';

import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import Block from '@mui/icons-material/Block';
import Clear from '@mui/icons-material/Clear';
import Edit from '@mui/icons-material/Edit';
import GroupAdd from '@mui/icons-material/GroupAdd';
import Save from '@mui/icons-material/Save';
import ViewTimeline from '@mui/icons-material/ViewTimeline';
import Warning from '@mui/icons-material/Warning';

import { Slots } from '../Slots/Slots';

const isNewShift = (id) => /^new-\d+(-\d+)?$/.test(id);

const formattedTime = (time) => {
    if (!time) {
        return '';
    }

    return /^\d{2}:\d{2}(:\d{2})?$/.test(time)
        ? DateTime.fromFormat(time, "HH:mm").toFormat("HH:mm")
        : DateTime.fromMillis(time).toFormat("HH:mm")
};

// MARK: Assign
const AssignPatrollersDialog = ({ open, selectedShifts, patrollers, roles, onClose, onAssign }) => {
    const [ assignments, setAssignments ] = useState([]);
    const [ initialAssignments, setInitialAssignments ] = useState([]);

    // Initialize assignments from existing shift.patrollers
    useEffect(() => {
        if (open && selectedShifts) {
            const existingAssignments = [];

            selectedShifts.forEach(shift => {
                if (shift.patrollers) {
                    // Group patrollers by role to properly assign slot indices
                    const patrollersByRole = {};
                    
                    shift.patrollers.forEach(p => {
                        if (!patrollersByRole[p.role]) {
                            patrollersByRole[p.role] = [];
                        }
                        patrollersByRole[p.role].push(p);
                    });

                    // Assign slot indices based on position within role group
                    Object.entries(patrollersByRole).forEach(([roleId, patrollerList]) => {
                        patrollerList.forEach((p, index) => {
                            existingAssignments.push({
                                patrollerId: p.id,
                                shiftId: shift.id,
                                roleId: p.role,
                                slotIndex: index
                            });
                        });
                    });
                }
            });

            setAssignments(existingAssignments);
            setInitialAssignments(existingAssignments);
        }
    }, [ open, selectedShifts ]);

    const handlePatrollerChange = (shiftId, roleId, slotIndex, patrollerId) => {
        setAssignments(prev => {
            // Remove any existing assignment for this shiftId, roleId, and slotIndex
            const filtered = prev.filter(a => !(a.shiftId === shiftId && a.roleId === roleId && a.slotIndex === slotIndex));

            // Add new assignment if patrollerId is not empty
            if (patrollerId) {
                return [ ...filtered, { patrollerId, shiftId, roleId, slotIndex } ];
            }

            return filtered;
        });
    };

    const getCurrentAssignment = (shiftId, roleId, slotIndex) => {
        const assignment = assignments.find(a => a.shiftId === shiftId && a.roleId === roleId && a.slotIndex === slotIndex);
        return assignment ? assignment.patrollerId : '';
    };

    const handleAssign = () => {
        onAssign(assignments, initialAssignments);

        setAssignments([]);
        setInitialAssignments([]);

        onClose();
    };

    const handleClose = () => {
        setAssignments([]);
        setInitialAssignments([]);

        onClose();
    };

    return (
        <Dialog open={ open } onClose={ handleClose } maxWidth="md" fullWidth>
            <DialogTitle>Assign Patrollers to Shifts</DialogTitle>

            <DialogContent>
                { (selectedShifts || []).map(shift => (
                    <Box key={ shift.id } sx={ { mb: 3 } }>
                        <Typography variant="h6" sx={ { mb: 2 } }>
                            { shift.name } - { DateTime.fromISO(shift.date).toLocaleString({ weekday: 'short', month: 'short', day: 'numeric' }) }
                        </Typography>

                        { shift.slots?.map(slot => {
                            const role = roles.find(r => r.id === slot.role);

                            return (
                                <Box key={ slot.role } sx={ { display: 'flex', alignItems: 'center', mb: 1 } }>
                                    <Typography sx={ { minWidth: 150, mr: 2 } }>
                                        { role?.name || 'Unknown Role' }
                                    </Typography>

                                    { Array.from({ length: slot.count || 0 }).map((_, index) => (
                                        <Box key={ `${ slot.role }-slot-${ index }` } sx={ { mr: 2 } }>
                                            <FormControl sx={ { minWidth: 200 } }>
                                                <InputLabel>Assign Patroller</InputLabel>

                                                <Select
                                                    value={ getCurrentAssignment(shift.id, slot.role, index) }
                                                    onChange={ (e) => handlePatrollerChange(shift.id, slot.role, index, e.target.value) }
                                                    label="Assign Patroller"
                                                >
                                                    <MenuItem value="">
                                                        <em>None</em>
                                                    </MenuItem>

                                                    { (patrollers || []).map(patroller => (
                                                        <MenuItem key={ patroller.id } value={ patroller.id }>
                                                            { patroller.name }
                                                        </MenuItem>
                                                    )) }
                                                </Select>
                                            </FormControl>
                                        </Box>
                                    )) }
                                </Box>
                            );
                        }) }
                        <Divider sx={ { mt: 2 } } />
                    </Box>
                )) }
            </DialogContent>

            <DialogActions>
                <Button onClick={ handleClose }>Cancel</Button>
                <Button onClick={ handleAssign } variant="contained">Assign</Button>
            </DialogActions>
        </Dialog>
    );
};

// MARK: Edit
const EditShift = ({ shift, onChange }) => {
    const handleDateChange = (newDate) => {
        const isoDate = newDate ? newDate.toISODate() : null;

        onChange({ target: { name: 'date', value: isoDate } });
    };

    return (
        <Box sx={ { width: '100%' } }>
            <Divider variant='middle' sx={ { my: 3 } } >
                <Chip label="Shift Config" color='primary' />
            </Divider>

            <Grid container spacing={ 1 }>
                <Grid size={ 3 }>
                    <TextField
                        id={ `shiftName-${ shift.id }` }
                        name="name"
                        label='Shift Name'
                        value={ shift.name }
                        onChange={ onChange }
                    />
                </Grid>

                <Grid size={ 3 }>
                    <LocalizationProvider dateAdapter={ AdapterLuxon }>
                        <DatePicker
                            label="Shift Date"
                            id={ `shiftDate-${ shift.id }` }
                            name="date"
                            value={ shift.date ? DateTime.fromISO(shift.date) : null }
                            onChange={ handleDateChange } />
                    </LocalizationProvider>
                </Grid>

                <Grid size={ 2 }>
                    <LocalizationProvider dateAdapter={ AdapterLuxon }>
                        <TimePicker
                            label="Start"
                            id={ `shiftStartTime-${ shift.id }` }
                            name="start"
                            format="HH:mm"
                            value={ shift.start ? (typeof shift.start === 'string' ? DateTime.fromFormat(shift.start, "HH:mm") : DateTime.fromMillis(shift.start)) : null }
                            onChange={ (newValue) => {
                                const timeValue = newValue ? newValue.toFormat("HH:mm") : '';

                                onChange({ target: { name: 'start', value: timeValue } });
                            } } />
                    </LocalizationProvider>
                </Grid>

                <Grid size={ 2 }>
                    <LocalizationProvider dateAdapter={ AdapterLuxon }>
                        <TimePicker
                            label="End"
                            id={ `shiftEndTime-${ shift.id }` }
                            name="end"
                            format="HH:mm"
                            value={ shift.end ? (typeof shift.end === 'string' ? DateTime.fromFormat(shift.end, "HH:mm") : DateTime.fromMillis(shift.end)) : null }
                            onChange={ (newValue) => {
                                const timeValue = newValue ? newValue.toFormat("HH:mm") : '';

                                onChange({ target: { name: 'end', value: timeValue } });
                            } } />
                    </LocalizationProvider>
                </Grid>

                <Grid size={ 2 }>
                    <FormControl fullWidth>
                        <InputLabel id={ `recurrence-${ shift.id }-label` }>Repeat</InputLabel>

                        <Select
                            labelId={ `recurrence-${ shift.id }-label` }
                            id={ `recurrence-${ shift.id }` }
                            name="recurrence"
                            value={ shift.recurrence || '' }
                            onChange={ onChange }
                            label="Repeat"
                            MenuProps={ { disableScrollLock: true } }
                            disabled={ isNewShift(shift.id) ? false : !!shift.recurrence }
                        >
                            <MenuItem value=""><em>None</em></MenuItem>
                            <MenuItem value="daily">Daily</MenuItem>
                            <MenuItem value="weekly">Weekly</MenuItem>
                            <MenuItem value="monthly">Monthly</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>
        </Box>
    );
};

// MARK: Show
const ShowShift = ({ shift, patrollers, roles, readOnly, selectedShifts, onAssignPatrollers, onEditShift, onCheckboxChange, integrityIssues }) => {
    const slots = Array.isArray(shift.slots) ? shift.slots : [];

    // filter out undefined counts
    const definedSlots = slots.filter(slot => slot.role !== undefined);
    const slotsCount = definedSlots.reduce((sum, slot) => sum + (slot.count || 0), 0);
    const availableCount = Number(shift.available) ? shift.available : 0;

    const handleAssignPatrollers = () => {
        onAssignPatrollers();
    };

    const handleEditShift = () => {
        if (selectedShifts.length <= 1) {
            onEditShift();
        }
    };

    const handleCheckboxChange = (e) => {
        onCheckboxChange(shift, e.target.checked);
    };

    const getSlotNames = (shiftSlots) => {
        if (shiftSlots.length === 0) {
            return <span>No slots assigned</span>;
        }

        return shiftSlots.map((slot, index) => {
            const role = roles.find(r => r.id === slot.role)?.name;

            return (
                <span key={ index }>
                    { role }: { slot.count }
                </span>
            );
        });
    };

    const getPatrollersRoles = (shiftPatrollers) => {
        if (!shiftPatrollers || shiftPatrollers.length === 0) {
            return <span>No patrollers assigned</span>;
        }

        return shiftPatrollers.map((sp, index) => {
            const name = patrollers.find(p => p.id === sp.id)?.name;
            const role = roles.find(r => r.id === sp.role)?.name;

            return (
                <span key={ index }>
                    { role }: { name }
                </span>
            );
        });
    };

    return (
        <Grid container spacing={ 1 } sx={ { width: '100%', color: shift.cancelled ? 'text.disabled' : 'inherit' } }>
            <Grid className="shift-faded" sx={ { px: 1 } } size={ 1 } display={ readOnly ? 'none' : 'flex' } justifyContent='center' alignItems='center'>
                <Checkbox
                    id={ `${ shift.id }` }
                    name={ `${ shift.id }` }
                    checked={ selectedShifts.some(s => s.id === shift.id) }
                    onChange={ handleCheckboxChange }
                />
            </Grid>

            <Grid sx={ { px: 1 } } size={ readOnly ? 4 : 3 } display='flex' justifyContent='space-between' alignItems='center'>
                <Box sx={ { display: 'flex', alignItems: 'center' } }>
                    <Box className="shift-faded" sx={ { display: 'flex', alignItems: 'center' } }>
                        <Typography noWrap sx={ { mr: 2 }}>{ shift.name }</Typography>

                        { integrityIssues && integrityIssues.length > 0 && (
                            <Tooltip title={ integrityIssues.map(issue => issue.message).join('; ') }>
                                <Icon
                                    component={ Warning }
                                    color='error'
                                    sx={ {
                                        verticalAlign: 'middle',
                                        ml: 0.5,
                                        fontSize: '1rem'
                                    } } />
                            </Tooltip>
                        ) }
                    </Box>

                    { shift.cancelled && (
                        <Chip
                            label="Cancelled"
                            color="error"
                            size="small"
                            variant="outlined"
                        />
                    ) }
                </Box>

                <Tooltip title={ "Timelogs exist for this shift, so it cannot be removed." }>
                    <Icon
                        component={ ViewTimeline }
                        color='warning'
                        sx={ {
                            display: shift.timelogs?.length > 0 ? 'flex' : 'none',
                            verticalAlign: 'middle',
                            ml: 0.5
                        } } />
                </Tooltip>
            </Grid>

            <Grid sx={ { px: 1 } } size={ readOnly ? 4 : 3 } display='flex' alignItems='center'>
                <Typography sx={{ textDecoration: shift.cancelled ? 'line-through' : 'none' }}>
                    { DateTime.fromISO(shift.date).toLocaleString({ weekday: 'short', month: 'short', day: 'numeric' }) }
                </Typography>
            </Grid>

            <Grid sx={ { px: 1 } } size={ 1 } display='flex' justifyContent='center' alignItems='center'>
                <Typography sx={{ textDecoration: shift.cancelled ? 'line-through' : 'none' }}>{ formattedTime(shift.start) }</Typography>
            </Grid>

            <Grid sx={ { px: 1 } } size={ 1 } display='flex' justifyContent='center' alignItems='center'>
                <Typography sx={{ textDecoration: shift.cancelled ? 'line-through' : 'none' }}>{ formattedTime(shift.end) }</Typography>
            </Grid>

            <Grid sx={ { px: 1 } } size={ 1 } display='flex' justifyContent='center' alignItems='center'>
                <Tooltip title={ <div className={ styles.assigned }>{ getPatrollersRoles(shift.patrollers) }</div> }>
                    <Typography className={ availableCount > 0 ? styles.available : undefined } sx={{ textDecoration: shift.cancelled ? 'line-through' : 'none' }}>
                        { availableCount }
                    </Typography>
                </Tooltip>
            </Grid>

            <Grid sx={ { px: 1 } } size={ 1 } display='flex' justifyContent='center' alignItems='center'>
                <Tooltip title={ <div className={ styles.assigned }>{ getSlotNames(shift.slots) }</div> }>
                    <Typography className={ slotsCount === 0 ? styles.available : undefined } sx={{ textDecoration: shift.cancelled ? 'line-through' : 'none' }}>
                        { slotsCount }
                    </Typography>
                </Tooltip>
            </Grid>

            { !shift.cancelled && <Grid sx={ { px: 1 } } size={ 1 } display={ readOnly ? 'none' : 'flex' } justifyContent='center' alignItems='center'>
                { selectedShifts.length > 0 && selectedShifts.some(s => s.id === shift.id) ? (
                    <IconButton variant="outlined" color="primary" onClick={ handleAssignPatrollers } >
                        <GroupAdd />
                    </IconButton>
                ) : (
                    <IconButton variant="outlined" color="primary" onClick={ handleEditShift } >
                        <Edit />
                    </IconButton>
                ) }
            </Grid> }
        </Grid>
    )
};

// MARK: Shift
export const Shift = ({ shift, roles, patrollers, readOnly, selectedShifts, onCheckboxChange, onSaveShift, onCancelEditShift, onCancelShift, onAssignPatrollers, integrityIssues }) => {
    const [ editShift, setEditShift ] = useState(isNewShift(shift.id) ? shift : null);
    const [ assignDialogOpen, setAssignDialogOpen ] = useState(false);
    const [ canSave, setCanSave ] = useState(false);

    // Cancel dialog state
    const [ cancelDialogOpen, setCancelDialogOpen ] = useState(false);
    const [ sendNotifications, setSendNotifications ] = useState(true);
    const [ canceling, setCanceling ] = useState(false);
    const [ error, setError ] = useState(null);

    const handleShiftChange = (e) => {
        const { name, value } = e.target;

        setEditShift(prev => ({ ...prev, [ name ]: value }));
    };

    const handleSlotChange = (slots) => {
        setEditShift(prev => ({ ...prev, slots }));
    };

    const handleAssignPatrollers = () => {
        setAssignDialogOpen(true);
    };

    const handleEditShift = () => {
        setEditShift(shift);
    };

    const handleSaveShift = () => {
        onSaveShift(editShift);

        setEditShift(null);
    };

    const handleCancelShift = () => {
        setCancelDialogOpen(true);
    }

    const handleConfirmCancel = async () => {
        setCanceling(true);
        setError(null);

        try {
            if (typeof onCancelShift === 'function') {
                await onCancelShift(shift.id, sendNotifications);
            } else {
                onCancelEditShift();
            }

            // Close the editor
            setEditShift(null);
        } catch (err) {
            setError(err.message || 'Failed to cancel shift');
        } finally {
            setCanceling(false);
            setCancelDialogOpen(false);
        }
    }

    const handleClose = () => {
        setEditShift(null);
    };

    const handleAssign = (assignments, initialAssignments) => {
        onAssignPatrollers(assignments, initialAssignments);
    };

    const handleCloseDialog = () => {
        setAssignDialogOpen(false);
    };

    useEffect(() => {
        setCanSave(!!editShift?.name && !!editShift?.date && !!editShift?.start && !!editShift?.end);
    }, [ editShift ]);

    return (
        <>
            { (editShift || isNewShift(shift.id)) && !readOnly ?
                <div className={ styles.edit }>
                    <EditShift shift={ editShift } onChange={ handleShiftChange } />

                    <Slots shift={ editShift } roles={ roles } onChange={ handleSlotChange } />

                    <Divider flexItem sx={ { mt: 3, mb: 2 } } />

                    <div className={ styles.actions }>
                        <Button variant="outlined" color="secondary" onClick={ handleCancelShift } startIcon={ <Block /> }>{ shift.cancelled ? 'Reset' : 'Cancel' } Shift</Button>

                        <Divider flexItem orientation="vertical" sx={ { mx: 1 } } />

                        <Button variant="outlined" color="secondary" onClick={ handleClose } startIcon={ <Clear /> }>Close</Button>
                        <Button variant="contained" color="primary" onClick={ handleSaveShift } disabled={ !canSave } startIcon={ <Save /> }>Save Shift</Button>
                    </div>

                    {/* Cancel Shift Confirmation Dialog */}
                    <Dialog open={ cancelDialogOpen } onClose={ () => setCancelDialogOpen(false) } maxWidth="sm" fullWidth>
                        <DialogTitle>Cancel Shift</DialogTitle>

                        <DialogContent>
                            <Typography>Are you sure you want to cancel this shift?</Typography>
                            <Typography variant='caption' sx={ { mt: 1 } } color='warning.main'>This does not delete the shift from the system, it only marks it as cancelled.</Typography>

                            <Box sx={ { mt: 2 } }>
                                <FormControlLabel
                                    control={<Checkbox checked={ sendNotifications } onChange={ (e) => setSendNotifications(e.target.checked) } />}
                                    label="Send notifications to assigned patrollers"
                                />

                                { error && (
                                    <Typography color="error" sx={ { mt: 1 } }>{ error }</Typography>
                                ) }
                            </Box>
                        </DialogContent>

                        <DialogActions>
                            <Button onClick={ () => setCancelDialogOpen(false) } disabled={ canceling }>No</Button>
                            <Button onClick={ handleConfirmCancel } variant="contained" color="error" disabled={ canceling }>{ canceling ? 'Cancelling...' : 'Yes, cancel shift' }</Button>
                        </DialogActions>
                    </Dialog>
                </div>
                :
                <ShowShift shift={ shift } patrollers={ patrollers } roles={ roles } readOnly={ readOnly } selectedShifts={ selectedShifts } onEditShift={ handleEditShift } onCheckboxChange={ onCheckboxChange } onAssignPatrollers={ handleAssignPatrollers } integrityIssues={ integrityIssues } />
            }

            <AssignPatrollersDialog
                open={ assignDialogOpen }
                selectedShifts={ selectedShifts }
                patrollers={ patrollers }
                roles={ roles }
                onClose={ handleCloseDialog }
                onAssign={ handleAssign }
            />
        </>
    );
};